import { apiClient } from '../../core/api';

export type OverviewDimensionValue =
  | '12 months'
  | '12 weeks'
  | '4 weeks'
  | '7 days'
  | 'yesterday'
  | 'today';

export interface TrendItem {
  type: 'conversation' | 'lead' | 'visitor';
  thisPeriodNum: number;
  rate: string;
}

export interface DataPoint {
  count: number;
  dateTime: string;
}

export interface DataStatistic {
  type: 'conversation' | 'lead' | 'visitor';
  dimension: string;
  dataList?: DataPoint[];
  totalNumber: number;
}

export interface OverviewResponse {
  trendList: TrendItem[];
  dataStatisticList: DataStatistic[];
}

export class OverviewService {
  /**
   * Get overview dashboard data
   */
  static async getDashboardData(
    userId: string,
    dimension: OverviewDimensionValue = '4 weeks',
    endDateTime?: string
  ): Promise<OverviewResponse> {
    // Format the date based on dimension if not provided
    if (!endDateTime) {
      const now = new Date();

      // For "yesterday" dimension, use yesterday's date as endDateTime
      if (dimension === 'yesterday') {
        now.setDate(now.getDate() - 1);
      }

      // Set local time to 23:59:59
      now.setHours(23, 59, 59, 0);
      // Convert to UTC string in dd-MM-yyyy HH:mm:ss
      const utcDay = String(now.getUTCDate()).padStart(2, '0');
      const utcMonth = String(now.getUTCMonth() + 1).padStart(2, '0');
      const utcYear = now.getUTCFullYear();
      const utcHours = String(now.getUTCHours()).padStart(2, '0');
      const utcMinutes = String(now.getUTCMinutes()).padStart(2, '0');
      const utcSeconds = String(now.getUTCSeconds()).padStart(2, '0');
      endDateTime = `${utcDay}-${utcMonth}-${utcYear} ${utcHours}:${utcMinutes}:${utcSeconds}`;
    }

    // Pass parameters directly - axios handles encoding automatically
    const params: Record<string, string> = {
      dimension: dimension,
      endDateTime: endDateTime,
    };

    const response = await apiClient.get<OverviewResponse>(`/dashboard/${userId}`, params);

    return response.data;
  }

  /**
   * Transform API data for chart display
   */
  static transformChartData(dataStatisticList: DataStatistic[]) {
    const conversationData = dataStatisticList.find(item => item.type === 'conversation');
    const leadData = dataStatisticList.find(item => item.type === 'lead');

    // TODO: Remove frontend UTC coercion once backend returns timezone-aware timestamps.
    // Helper to parse either `dd-MM-yyyy HH:mm:ss` or `yyyy-MM-dd HH:mm:ss` as UTC
    function parseDateTime(dateTime: string): Date {
      const [datePart, timePart = '00:00:00'] = dateTime.split(' ');
      const parts = datePart.split('-');

      let year = parts[0];
      let month = parts[1];
      let day = parts[2];

      // If the first part is not a 4-digit year, assume dd-MM-yyyy
      if (year.length !== 4) {
        day = parts[0];
        month = parts[1];
        year = parts[2];
      }

      // Ensure two-digit month/day for ISO parsing and force UTC
      const iso = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}T${timePart}Z`;
      return new Date(iso);
    }

    function formatLabel(date: Date, dimension: OverviewDimensionValue): string {
      switch (dimension) {
        case 'today':
        case 'yesterday':
          return date.toLocaleTimeString('en-US', { hour: '2-digit' });
        case '12 months':
          return date.toLocaleDateString('en-US', { month: 'short' });
        case '12 weeks':
        case '4 weeks':
        case '7 days':
        default:
          return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      }
    }

    // Generate default empty data based on dimension
    function generateEmptyData(dimension: OverviewDimensionValue) {
      const now = new Date();
      const data: { date: string; newConversations: number; newLeads: number }[] = [];

      switch (dimension) {
        case 'today':
        case 'yesterday': {
          // 24 hours
          for (let i = 0; i < 24; i++) {
            const hour = i.toString().padStart(2, '0');
            data.push({ date: `${hour}:00`, newConversations: 0, newLeads: 0 });
          }
          break;
        }
        case '7 days': {
          for (let i = 6; i >= 0; i--) {
            const date = new Date(now);
            date.setDate(date.getDate() - i);
            data.push({ date: formatLabel(date, dimension), newConversations: 0, newLeads: 0 });
          }
          break;
        }
        case '4 weeks': {
          for (let i = 27; i >= 0; i--) {
            const date = new Date(now);
            date.setDate(date.getDate() - i);
            data.push({ date: formatLabel(date, dimension), newConversations: 0, newLeads: 0 });
          }
          break;
        }
        case '12 weeks': {
          for (let i = 11; i >= 0; i--) {
            const date = new Date(now);
            date.setDate(date.getDate() - i * 7);
            data.push({ date: formatLabel(date, dimension), newConversations: 0, newLeads: 0 });
          }
          break;
        }
        case '12 months': {
          for (let i = 11; i >= 0; i--) {
            const date = new Date(now);
            date.setMonth(date.getMonth() - i);
            data.push({ date: formatLabel(date, dimension), newConversations: 0, newLeads: 0 });
          }
          break;
        }
        default: {
          for (let i = 6; i >= 0; i--) {
            const date = new Date(now);
            date.setDate(date.getDate() - i);
            data.push({ date: formatLabel(date, dimension), newConversations: 0, newLeads: 0 });
          }
        }
      }
      return data;
    }

    const dimension = (conversationData?.dimension as OverviewDimensionValue) || '4 weeks';

    // If no data, return empty data with correct labels for the dimension
    if (!conversationData?.dataList?.length || !leadData?.dataList?.length) {
      return generateEmptyData(dimension);
    }

    // Combine data points by date/time with proper label formatting
    return conversationData.dataList.map((item, index) => {
      const parsedDate = parseDateTime(item.dateTime);
      const leadItem = leadData.dataList?.[index];

      return {
        date: formatLabel(parsedDate, dimension),
        newConversations: item.count,
        newLeads: leadItem?.count || 0,
      };
    });
  }

  /**
   * Get dimension parameter based on period
   */
  static getDimensionFromPeriod(period: string): OverviewDimensionValue {
    const dimensionMap: Record<string, OverviewDimensionValue> = {
      '12months': '12 months',
      '12weeks': '12 weeks',
      '30days': '4 weeks',
      '7days': '7 days',
      yesterday: 'yesterday',
      today: 'today',
    };

    return dimensionMap[period] || '4 weeks';
  }
}
